import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {import('./slideshow').Slideshow} [slideshow]
 * @property {HTMLElement[]} [media]
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Variant update:
   * Use native View Transitions on the main media item only,
   * instead of animating the whole gallery container.
   *
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = async (event) => {
    const source = event.detail?.data?.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    const oldMainMedia = this.#getMainMedia(this);
    const newMainMedia = this.#getMainMedia(newMediaGallery);

    // no main media found -> fallback
    if (!oldMainMedia || !newMainMedia) {
      this.replaceWith(newMediaGallery);
      return;
    }

    // Give both old/new main media the same view transition name
    oldMainMedia.style.viewTransitionName = 'variant-media-transition';
    newMainMedia.style.viewTransitionName = 'variant-media-transition';

    if (document.startViewTransition) {
      try {
        const transition = document.startViewTransition(() => {
          this.replaceWith(newMediaGallery);
        });

        await transition.finished;
      } catch (error) {
        this.replaceWith(newMediaGallery);
      }
    } else {
      this.replaceWith(newMediaGallery);
    }
  };

  /**
   * Find the "main" product media container.
   * In this theme, the selected variant media is sorted to the first position.
   * So the first product-media-container is the correct transition target.
   *
   * @param {Element} root
   * @returns {HTMLElement | null}
   */
  #getMainMedia(root) {
    return root.querySelector(
      '[data-view-transition-type="product-image-transition"], .product-media-container'
    );
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index
   * @param {PointerEvent} event
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}